import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { Loader2, Users } from "lucide-react";

const BUCKET_ORDER = ["Not due", "Due soon (30d)", "Overdue 1-30d", "Overdue 31-60d", "Overdue 60+d"];
// UI label rename: backend bucket key stays "Not due" for API compat; we display "Net Due" in the header.
const BUCKET_TONE = {
  "Not due": "text-muted-foreground",
  "Due soon (30d)": "text-brand",
  "Overdue 1-30d": "text-warning",
  "Overdue 31-60d": "text-warning",
  "Overdue 60+d": "text-danger",
};

export function PartyAging() {
  const { currency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/receivables/aging-by-party?currency=${currency}`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currency]);

  if (loading) {
    return (
      <Card className="p-8 flex items-center justify-center" data-testid="party-aging-loading">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
      </Card>
    );
  }
  if (!data || !data.parties?.length) {
    return (
      <Card className="p-6" data-testid="party-aging-empty">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center">
            <Users className="h-4 w-4 text-brand" />
          </div>
          <div>
            <h3 className="font-heading text-sm font-semibold">Party-wise Aging</h3>
            <p className="text-xs text-muted-foreground">Add receivables to see per-client outstanding buckets here.</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5" data-testid="party-aging">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center">
          <Users className="h-4 w-4 text-brand" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">Party-wise Aging</h3>
          <p className="text-xs text-muted-foreground">
            {data.parties.length} client{data.parties.length === 1 ? "" : "s"} · Outstanding{" "}
            <span className="font-mono text-foreground">{formatMoney(data.grand_total, currency)}</span>
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="party-aging-table">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-2 py-2">Client</th>
              <th className="text-right px-2 py-2">Invoices</th>
              {BUCKET_ORDER.map((b) => (
                <th key={b} className="text-right px-2 py-2">{b === "Not due" ? "Net Due" : b}</th>
              ))}
              <th className="text-right px-2 py-2 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.parties.map((p) => (
              <tr key={p.client} className="border-t border-border hover:bg-muted/40" data-testid={`party-row-${p.client}`}>
                <td className="px-2 py-2 font-medium">{p.client}</td>
                <td className="px-2 py-2 text-right text-muted-foreground">{p.count}</td>
                {BUCKET_ORDER.map((b) => (
                  <td key={b} className={`px-2 py-2 text-right font-mono ${BUCKET_TONE[b] || ""}`}>
                    {p.buckets[b] ? formatMoney(p.buckets[b], currency, { compact: true }) : "—"}
                  </td>
                ))}
                <td className="px-2 py-2 text-right font-mono font-semibold">{formatMoney(p.total, currency, { compact: true })}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-border bg-muted/30 font-semibold">
              <td className="px-2 py-2">Total</td>
              <td className="px-2 py-2 text-right">—</td>
              {BUCKET_ORDER.map((b) => (
                <td key={b} className={`px-2 py-2 text-right font-mono ${BUCKET_TONE[b] || ""}`}>
                  {data.totals[b] ? formatMoney(data.totals[b], currency, { compact: true }) : "—"}
                </td>
              ))}
              <td className="px-2 py-2 text-right font-mono">{formatMoney(data.grand_total, currency, { compact: true })}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
